import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { matchesSkillQuery } from '../utils/skillSearch';
import SkillFormModal from '../components/SkillFormModal';
import LevelRoutineEditor from '../components/routines/LevelRoutineEditor';
import LevelFormModal from '../components/levels/LevelFormModal';
import { totalDifficulty, markRepeats, difficultyFor } from '../utils/routineDifficulty';
import '../components/routines/routineRepeat.css';
import './booking/booking-shared.css';
import './Levels.css';

const Levels = () => {
  const [levels, setLevels] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [showAddSkillForm, setShowAddSkillForm] = useState(null);
  const [showAttachExistingSkill, setShowAttachExistingSkill] = useState(null);
  const [showAddRoutineForm, setShowAddRoutineForm] = useState(null);
  const [showAddLevelForm, setShowAddLevelForm] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState(new Set());
  const [expandedRoutines, setExpandedRoutines] = useState(new Set());
  const [editMode, setEditMode] = useState(false);
  const [discipline, setDiscipline] = useState('TRA'); // TRA or DMT tab
  const { canEditLevels } = useAuth();
  const disciplineOf = (levelId) => levels.find(l => l.id === levelId)?.discipline || 'TRA';

  // Close any open modals when edit mode is turned off
  const levelLabel = (levelId) => {
    const level = levels.find(l => l.id === levelId);
    return level ? `Level ${level.identifier}` : '';
  };

  const toggleEditMode = () => {
    if (editMode) {
      // Close all open modals when exiting edit mode
      setEditingLevel(null);
      setEditingSkill(null);
      setEditingRoutine(null);
      setShowAddSkillForm(null);
      setShowAddRoutineForm(null);
      setShowAddLevelForm(false);
    }
    setEditMode(!editMode);
  };

  useEffect(() => {
    if (canEditLevels) {
      fetchLevels();
    }
  }, [canEditLevels]);

  const fetchAvailableSkills = useCallback(async () => {
    try {
      // Use the first level ID to get available skills (endpoint works with any level)
      const response = await axios.get(`/api/levels/${levels[0].id}/available-skills`);
      setAvailableSkills(response.data);
    } catch (error) {
      console.error('Failed to fetch available skills:', error);
    }
  }, [levels]);

  useEffect(() => {
    if (canEditLevels && levels.length > 0) {
      fetchAvailableSkills();
    }
  }, [canEditLevels, levels, fetchAvailableSkills]);

  // Only staff who manage skill setup can open this page
  if (!canEditLevels) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Access Denied</h3>
        </div>
        <div>
          <p>You don't have permission to access the levels management page.</p>
          <p>You need Manage access to skill setup to change levels.</p>
        </div>
      </div>
    );
  }

  const fetchLevels = async () => {
    try {
      const response = await axios.get('/api/levels');
      setLevels(response.data);
      // Collapsed by default
      setExpandedLevels(new Set());
    } catch (error) {
      console.error('Failed to fetch levels:', error);
      setError('Failed to load levels');
    } finally {
      setLoading(false);
    }
  };

  // Remove duplicate function declaration - using useCallback version above

  const toggleLevelExpansion = (levelId) => {
    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(levelId)) {
      newExpanded.delete(levelId);
    } else {
      newExpanded.add(levelId);
    }
    setExpandedLevels(newExpanded);
  };

  const toggleRoutineExpansion = (routineId) => {
    const newExpanded = new Set(expandedRoutines);
    if (newExpanded.has(routineId)) {
      newExpanded.delete(routineId);
    } else {
      newExpanded.add(routineId);
    }
    setExpandedRoutines(newExpanded);
  };

  const handleUpdateLevel = async (levelId, levelData) => {
    try {
      const response = await axios.put(`/api/levels/${levelId}`, levelData);
      setLevels(levels.map(level => 
        level.id === levelId ? response.data : level
      ));
      setEditingLevel(null);
    } catch (error) {
      console.error('Failed to update level:', error);
      setError(error.response?.data?.error || 'Failed to update level');
    }
  };

  const handleCreateLevel = async (levelData) => {
    try {
      const response = await axios.post('/api/levels', levelData);
      setLevels([...levels, response.data]);
      setShowAddLevelForm(false);
      // Expand the new level automatically
      setExpandedLevels(prev => new Set([...prev, response.data.id]));
    } catch (error) {
      console.error('Failed to create level:', error);
      setError(error.response?.data?.error || 'Failed to create level');
    }
  };

  const handleCreateSkill = async (levelId, skillData) => {
    try {
      const response = await axios.post(`/api/levels/${levelId}/skills`, skillData);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            skills: [...level.skills, response.data]
          };
        }
        return level;
      }));
      setShowAddSkillForm(null);
    } catch (error) {
      console.error('Failed to create skill:', error);
      setError(error.response?.data?.error || 'Failed to create skill');
    }
  };

  const handleAttachExistingSkill = async (levelId, skillId) => {
    try {
      const response = await axios.post(`/api/levels/${levelId}/skills`, { skillId });
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return { ...level, skills: [...level.skills, response.data] };
        }
        return level;
      }));
      setShowAttachExistingSkill(null);
    } catch (error) {
      console.error('Failed to attach skill:', error);
      setError(error.response?.data?.error || 'Failed to attach skill');
    }
  };

  const handleUpdateSkill = async (levelId, skillId, skillData) => {
    try {
      const response = await axios.put(`/api/levels/${levelId}/skills/${skillId}`, skillData);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            skills: level.skills.map(skill => 
              skill.id === skillId ? response.data : skill
            )
          };
        }
        return level;
      }));
      setEditingSkill(null);
    } catch (error) {
      console.error('Failed to update skill:', error);
      setError(error.response?.data?.error || 'Failed to update skill');
    }
  };

  const handleDeleteSkill = async (levelId, skillId) => {
    if (!window.confirm('Are you sure you want to delete this skill? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/levels/${levelId}/skills/${skillId}`);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            skills: level.skills.filter(skill => skill.id !== skillId)
          };
        }
        return level;
      }));
    } catch (error) {
      console.error('Failed to delete skill:', error);
      setError(error.response?.data?.error || 'Failed to delete skill');
    }
  };

  const handleCreateRoutine = async (levelId, routineData) => {
    try {
      const response = await axios.post(`/api/levels/${levelId}/routines`, routineData);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            routines: [...level.routines, response.data]
          };
        }
        return level;
      }));
      setShowAddRoutineForm(null);
    } catch (error) {
      console.error('Failed to create routine:', error);
      setError(error.response?.data?.error || 'Failed to create routine');
    }
  };

  const handleUpdateRoutine = async (levelId, routineId, routineData) => {
    try {
      const response = await axios.put(`/api/levels/${levelId}/routines/${routineId}`, routineData);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            routines: level.routines.map(routine => 
              routine.id === routineId ? response.data : routine
            )
          };
        }
        return level;
      }));
      setEditingRoutine(null);
    } catch (error) {
      console.error('Failed to update routine:', error);
      setError(error.response?.data?.error || 'Failed to update routine');
    }
  };

  const handleDeleteRoutine = async (levelId, routineId) => {
    if (!window.confirm('Are you sure you want to delete this routine? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/levels/${levelId}/routines/${routineId}`);
      setLevels(levels.map(level => {
        if (level.id === levelId) {
          return {
            ...level,
            routines: level.routines.filter(routine => routine.id !== routineId)
          };
        }
        return level;
      }));
    } catch (error) {
      console.error('Failed to delete routine:', error);
      setError(error.response?.data?.error || 'Failed to delete routine');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
        <button 
          onClick={() => setError(null)}
          className="btn btn-sm btn-outline"
          style={{ marginLeft: '10px' }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  const isDmt = discipline === 'DMT';
  const tabLevels = levels.filter(level => (level.discipline || 'TRA') === discipline);
  const sequentialLevels = tabLevels.filter(level => level.type === 'SEQUENTIAL');
  const levelNumbers = sequentialLevels.map(level => level.number);
  const prefix = isDmt ? 'D' : '';
  const firstLevel = prefix + (levelNumbers.length ? Math.min(...levelNumbers) : 1);
  const lastLevel = prefix + (levelNumbers.length ? Math.max(...levelNumbers) : 1);
  const sidePaths = tabLevels.filter(level => level.type === 'SIDE_PATH');

  return (
    <div className="lv-page">
      <div className="lv-page__header">
        <h2 style={{ margin: 0 }}>{isDmt ? 'Double mini levels' : 'Trampoline levels'}</h2>
        {canEditLevels && (
          <div className="lv-page__actions">
            {editMode && (
              <button type="button" onClick={() => setShowAddLevelForm(true)} className="bk-btn bk-btn--outline bk-btn--sm">
                + Add level
              </button>
            )}
            <button type="button" onClick={toggleEditMode}
              className={`bk-btn bk-btn--sm ${editMode ? 'bk-btn--primary' : 'bk-btn--outline'}`}>
              {editMode ? 'Done' : 'Edit'}
            </button>
          </div>
        )}
      </div>
      <div className="lv-tabs" role="tablist" aria-label="Discipline">
        {[['TRA', 'Trampoline'], ['DMT', 'Double mini']].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={discipline === value}
            className={`lv-tab${discipline === value ? ' lv-tab--on' : ''}`}
            onClick={() => setDiscipline(value)}>{label}</button>
        ))}
      </div>
      {editMode && (
        <p className="lv-muted" style={{ margin: '0 0 0.75rem' }}>
          Open a level to change its skills and routines. The pencil next to a level's name edits the level itself.
        </p>
      )}

      {tabLevels.length === 0 ? (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">No levels yet</h3>
          </div>
          <div>
            <p>No {isDmt ? 'double mini' : 'trampoline'} levels have been set up yet.</p>
            <p>{editMode ? 'Use + Add level to create the first one.' : 'Choose Edit, then + Add level to create the first one.'}</p>
          </div>
        </div>
      ) : (
        <div>
          {/* Sequential Levels */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Levels {firstLevel} to {lastLevel}</h3>
            </div>
            <div>
              <p className="lv-muted">
                Completed in order, from Level {firstLevel} to Level {lastLevel}.
                {isDmt && ' Each level has a few new skills, then passes (a mount or spotter, then a dismount) that must all be completed.'}
              </p>
              <div className="levels-container">
                {sequentialLevels.map(level => (
                  <LevelCard
                    key={level.id}
                    level={level}
                    canEdit={canEditLevels && editMode}
                    isExpanded={expandedLevels.has(level.id)}
                    onToggleExpansion={() => toggleLevelExpansion(level.id)}
                    onEditLevel={() => setEditingLevel(level)}
                    onUpdateLevel={handleUpdateLevel}
                    onAddSkill={() => setShowAddSkillForm(level.id)}
                    onAttachExistingSkill={() => setShowAttachExistingSkill(level.id)}
                    onEditSkill={setEditingSkill}
                    onUpdateSkill={handleUpdateSkill}
                    onDeleteSkill={handleDeleteSkill}
                    onAddRoutine={() => setShowAddRoutineForm(level.id)}
                    onEditRoutine={setEditingRoutine}
                    onUpdateRoutine={handleUpdateRoutine}
                    onDeleteRoutine={handleDeleteRoutine}
                    expandedRoutines={expandedRoutines}
                    onToggleRoutineExpansion={toggleRoutineExpansion}
                    availableSkills={availableSkills}
                    onSetPositions={(skillId, dmtPositions) => handleUpdateSkill(level.id, skillId, { dmtPositions })}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Side Paths */}
          {sidePaths.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Side paths</h3>
              </div>
              <div>
                <p className="lv-muted">Extra skill paths that can be completed alongside the main levels.</p>
                <div className="levels-container">
                  {sidePaths.map(level => (
                    <LevelCard
                      key={level.id}
                      level={level}
                      canEdit={canEditLevels && editMode}
                      isExpanded={expandedLevels.has(level.id)}
                      onToggleExpansion={() => toggleLevelExpansion(level.id)}
                      onEditLevel={() => setEditingLevel(level)}
                      onUpdateLevel={handleUpdateLevel}
                      onAddSkill={() => setShowAddSkillForm(level.id)}
                    onAttachExistingSkill={() => setShowAttachExistingSkill(level.id)}
                      onEditSkill={setEditingSkill}
                      onUpdateSkill={handleUpdateSkill}
                      onDeleteSkill={handleDeleteSkill}
                      onAddRoutine={() => setShowAddRoutineForm(level.id)}
                      onEditRoutine={setEditingRoutine}
                      onUpdateRoutine={handleUpdateRoutine}
                      onDeleteRoutine={handleDeleteRoutine}
                      expandedRoutines={expandedRoutines}
                      onToggleRoutineExpansion={toggleRoutineExpansion}
                      availableSkills={availableSkills}
                      onSetPositions={(skillId, dmtPositions) => handleUpdateSkill(level.id, skillId, { dmtPositions })}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Level Modal */}
      {editingLevel && editMode && (
        <LevelFormModal
          level={editingLevel}
          levels={levels}
          discipline={editingLevel.discipline || 'TRA'}
          onSave={(levelData) => handleUpdateLevel(editingLevel.id, levelData)}
          onCancel={() => setEditingLevel(null)}
        />
      )}

      {/* Edit Skill Modal */}
      {editingSkill && editMode && (
        <SkillFormModal
          mode="edit"
          skill={editingSkill}
          onSave={(skillData) => handleUpdateSkill(editingSkill.levelId, editingSkill.id, skillData)}
          onCancel={() => setEditingSkill(null)}
        />
      )}

      {/* Edit Routine Modal */}
      {editingRoutine && editMode && (
        <LevelRoutineEditor
          routine={editingRoutine}
          apparatus={disciplineOf(editingRoutine.levelId)}
          levelLabel={levelLabel(editingRoutine.levelId)}
          availableSkills={availableSkills}
          onSave={(routineData) => handleUpdateRoutine(editingRoutine.levelId, editingRoutine.id, routineData)}
          onCancel={() => setEditingRoutine(null)}
        />
      )}

      {/* Add Skill Modal */}
      {showAddSkillForm && editMode && (
        <SkillFormModal
          mode="add"
          onSave={(skillData) => handleCreateSkill(showAddSkillForm, skillData)}
          onCancel={() => setShowAddSkillForm(null)}
        />
      )}

      {/* Attach existing library skill */}
      {showAttachExistingSkill && editMode && (
        <AttachExistingSkillModal
          levelId={showAttachExistingSkill}
          existingSkillIds={(levels.find(l => l.id === showAttachExistingSkill)?.skills || []).map(s => s.id)}
          onSave={(skillId) => handleAttachExistingSkill(showAttachExistingSkill, skillId)}
          onCancel={() => setShowAttachExistingSkill(null)}
        />
      )}

      {/* Add Routine Modal */}
      {showAddRoutineForm && editMode && (
        <LevelRoutineEditor
          routine={null}
          apparatus={disciplineOf(showAddRoutineForm)}
          levelLabel={levelLabel(showAddRoutineForm)}
          availableSkills={availableSkills}
          onSave={(routineData) => handleCreateRoutine(showAddRoutineForm, routineData)}
          onCancel={() => setShowAddRoutineForm(null)}
        />
      )}

      {/* Add Level Modal */}
      {showAddLevelForm && editMode && (
        <LevelFormModal
          levels={levels}
          discipline={discipline}
          onSave={handleCreateLevel}
          onCancel={() => setShowAddLevelForm(false)}
        />
      )}
    </div>
  );
};

const POSITIONS = ['mount', 'spotter', 'dismount'];
const positionLabel = (p) => p.charAt(0).toUpperCase() + p.slice(1);

// Double mini levels: tick where on the double mini the skill is learned
const PositionPicker = ({ value, onChange }) => (
  <div className="lv-position-picker" role="group" aria-label="Where on the double mini">
    {POSITIONS.map(p => {
      const on = value.includes(p);
      return (
        <button key={p} type="button" aria-pressed={on}
          className={`lv-position${on ? ' lv-position--on' : ''}`}
          onClick={() => onChange(on ? value.filter(x => x !== p) : POSITIONS.filter(x => x === p || value.includes(x)))}>
          {positionLabel(p)}
        </button>
      );
    })}
  </div>
);

// LevelCard Component
const LevelCard = ({ 
  level, 
  canEdit, 
  isExpanded, 
  onToggleExpansion, 
  onEditLevel, 
  onUpdateLevel, 
  onAddSkill,
  onAttachExistingSkill,
  onEditSkill,
  onUpdateSkill, 
  onDeleteSkill,
  onAddRoutine,
  onEditRoutine,
  onUpdateRoutine,
  onDeleteRoutine,
  expandedRoutines,
  onToggleRoutineExpansion,
  availableSkills,
  onSetPositions,
}) => {
  const isDmt = level.discipline === 'DMT';
  const n = level.routines.length;
  const routineCount = isDmt ? `${n} pass${n === 1 ? '' : 'es'}` : `${n} routine${n === 1 ? '' : 's'}`;
  return (
    <div className={`lv-card${isExpanded ? ' lv-card--open' : ''}`}>
      <div className="lv-card__head">
        <button type="button" onClick={onToggleExpansion} className="lv-card__toggle" aria-expanded={isExpanded}>
          <span className="lv-card__title">Level {level.identifier || level.number}: {level.name}</span>
          <span className="lv-card__meta">
            {level.skills.length} skill{level.skills.length === 1 ? '' : 's'}
            {' · '}{routineCount}
          </span>
        </button>
        {canEdit && (
          <button type="button" onClick={onEditLevel} className="edit-mode-btn" title="Edit level" aria-label={`Edit level ${level.identifier || level.number}`}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
        )}
        <span className="lv-card__chevron" aria-hidden="true" />
      </div>

      {isExpanded && (
        <div className="level-content">
          {level.description && (
            <p className="level-description">{level.description}</p>
          )}
          
          {/* Skills Section */}
          <div className="skills-section">
            <div className="section-header">
              <h5>Skills ({level.skills.length})</h5>
              {/* Add Skill moved below list for better flow */}
            </div>
            
            {level.skills.length > 0 ? (
              <div className="skills-grid">
                {level.skills.map(skill => (
                  <div key={skill.id}>
                    <div className="routine-skill-item">
                      <span className="skill-name">{skill.name}</span>
                      {isDmt && !canEdit && skill.dmtPositions?.length > 0 && (
                        <span className="lv-positions">{skill.dmtPositions.map(positionLabel).join(', ')}</span>
                      )}
                      {canEdit && (
                        <span className="edit-mode-btn-group">
                          <button onClick={() => onEditSkill(skill)} className="edit-mode-btn" title="Edit skill">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button onClick={() => onDeleteSkill(level.id, skill.id)} className="edit-mode-btn edit-mode-btn--delete" title="Delete skill">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                        </span>
                      )}
                    </div>
                    {isDmt && canEdit && (
                      <PositionPicker value={skill.dmtPositions || []} onChange={(next) => onSetPositions(skill.id, next)} />
                    )}
                    {skill.description && (
                      <div className="skill-description" style={{ marginTop: '0.25rem' }}>{skill.description}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">No skills defined</p>
            )}
            {canEdit && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={onAddSkill} className="edit-mode-add-btn">+ Create new skill</button>
                <button onClick={onAttachExistingSkill} className="edit-mode-add-btn">+ Add from library</button>
              </div>
            )}
          </div>

          {/* Routines Section */}
          <div className="routines-section">
            <div className="section-header">
              <h5>{isDmt ? (level.minPassTariff != null ? 'Example passes' : 'Passes') : 'Routines'} ({level.routines.length})</h5>
              {canEdit && (
                <button onClick={onAddRoutine} className="edit-mode-add-btn">{isDmt ? '+ Add pass' : '+ Add Routine'}</button>
              )}
            </div>
            
            {isDmt && level.minPassTariff != null && (
              <p className="lv-target">Any two different passes of {Number(level.minPassTariff).toFixed(1)} or more complete this level, including the gymnast's own passes once a coach has checked them and marked them competition-ready.</p>
            )}
            {level.routines.length > 0 ? (
              <div className="lv-routines">
                {level.routines.map(routine => (
                  <RoutineCard
                    key={routine.id}
                    routine={routine}
                    apparatus={level.discipline || 'TRA'}
                    canEdit={canEdit}
                    isExpanded={expandedRoutines.has(routine.id)}
                    onToggleExpansion={() => onToggleRoutineExpansion(routine.id)}
                    onEditRoutine={() => onEditRoutine(routine)}
                    onDeleteRoutine={() => onDeleteRoutine(level.id, routine.id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted">{isDmt ? 'No passes defined' : 'No routines defined'}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// One routine: a header with its size and difficulty that opens to the numbered skills
const RoutineCard = ({
  routine,
  apparatus = 'TRA',
  canEdit,
  isExpanded,
  onToggleExpansion,
  onEditRoutine,
  onDeleteRoutine,
}) => {
  const totalDD = totalDifficulty(routine.skills, apparatus);
  const name = routine.name || `Routine ${routine.order}`;
  return (
    <div className={`lv-routine${isExpanded ? ' lv-routine--open' : ''}`}>
      <div className="lv-routine__head">
        <button type="button" className="lv-routine__toggle" onClick={onToggleExpansion} aria-expanded={isExpanded}>
          <span className="lv-routine__title">
            {name}
            {routine.isAlternative && <span className="lv-routine__tag">Alternative</span>}
            {routine.dmtStart && <span className="lv-routine__tag">{routine.dmtStart === 'spotter' ? 'Spotter' : 'Mount'} first</span>}
          </span>
          <span className="lv-routine__meta">
            {routine.skills.length} skill{routine.skills.length === 1 ? '' : 's'}
            {totalDD > 0 && <> · DD {totalDD.toFixed(1)}</>}
          </span>
        </button>
        {canEdit && (
          <span className="edit-mode-btn-group">
            <button onClick={onEditRoutine} className="edit-mode-btn" title="Edit routine" aria-label={`Edit ${name}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button onClick={onDeleteRoutine} className="edit-mode-btn edit-mode-btn--delete" title="Delete routine" aria-label={`Delete ${name}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            </button>
          </span>
        )}
        <span className="lv-card__chevron" aria-hidden="true" />
      </div>

      {isExpanded && (
        <div className="lv-routine__body">
          {routine.description && <p className="lv-routine__description">{routine.description}</p>}
          {routine.skills.length > 0 ? (
            <ol className="lv-routine__skills">
              {markRepeats(routine.skills).map((skill, idx) => {
                const own = difficultyFor(skill, apparatus);
                const dd = own == null ? null : skill.isRepeat ? 0 : own;
                return (
                  <li key={skill.id} className={`lv-routine__skill${skill.isRepeat ? ' is-repeat' : ''}${skill.isImplicit ? ' lv-routine__skill--implicit' : ''}`}>
                    <span className="lv-routine__num">{idx + 1}</span>
                    <span className="lv-routine__skill-name">
                      {skill.name}
                      {skill.isImplicit && <span className="routine-legacy-tag" title="Typed in before routines used the skills library. A club admin can swap it for a library skill.">Not in library</span>}
                      {skill.isRepeat && <span className="routine-repeat-tag" title="Repeated move: scores no difficulty">Repeat</span>}
                    </span>
                    {skill.figNotation && <code className="lv-routine__fig">{skill.figNotation}</code>}
                    <span className="lv-routine__dd">{dd != null ? dd.toFixed(1) : ''}</span>
                  </li>
                );
              })}
              {totalDD > 0 && (
                <li className="lv-routine__total">
                  <span>Total DD</span>
                  <span className="lv-routine__dd">{totalDD.toFixed(1)}</span>
                </li>
              )}
            </ol>
          ) : (
            <p className="lv-muted">No skills in this routine yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Add Skill Modal Component
// Search-driven picker for attaching an existing library skill to a level.
// Skills already in this level are filtered out.
const AttachExistingSkillModal = ({ levelId, existingSkillIds, onSave, onCancel }) => {
  const [allSkills, setAllSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/skills')
      .then(res => { if (!cancelled) setAllSkills(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load skills'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const matches = useMemo(() => {
    const existing = new Set(existingSkillIds || []);
    const all = allSkills
      .filter(s => !existing.has(s.id))
      .filter(s => matchesSkillQuery(query, s.name, s.figNotation));
    // Cap only when there's no query (browsing) — when typing, show every match.
    return query.trim() ? all : all.slice(0, 50);
  }, [allSkills, query, existingSkillIds]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Add existing skill to level</h3>
          <button onClick={onCancel} className="close-button">×</button>
        </div>
        <div style={{ padding: '0.5rem 0' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or FIG notation…"
            autoFocus
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #ccc', borderRadius: 4, fontSize: '1rem' }}
          />
          {error && <p className="bk-error" style={{ marginTop: '0.5rem' }}>{error}</p>}
          {loading && <p style={{ marginTop: '0.5rem' }}>Loading…</p>}

          {!loading && (
            <div style={{ maxHeight: 360, overflowY: 'auto', marginTop: '0.5rem', border: '1px solid #eee', borderRadius: 4 }}>
              {matches.length === 0 ? (
                <p style={{ margin: 0, padding: '1rem', color: '#888', textAlign: 'center' }}>No matches</p>
              ) : matches.map(s => {
                const levelLabels = (s.levels && s.levels.length > 0)
                  ? s.levels.map(l => `L${l.identifier}`).join(', ')
                  : 'Library';
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSave(s.id)}
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0.75rem',
                      border: 'none',
                      borderBottom: '1px solid #f3f3f3',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f6f6f8'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>
                      <strong>{s.name}</strong>
                      <span style={{ marginLeft: 8, color: '#888' }}>{levelLabels}</span>
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>
                      {s.figNotation || '—'} · {s.difficulty != null ? Number(s.difficulty).toFixed(1) : '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="modal-actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default Levels; 